import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Divider, Grid, makeStyles, Typography } from "@material-ui/core";
import DatePicker from "react-multi-date-picker";
import "./calendar.css";

import { Icon } from "../base/Icon";
import { COLORS } from "../../theme/colors";
import { Toggle } from "../../components/toggle";
import { SmallRectangleButton } from "../base/Button";
import { PERIODS } from "../../pages/dashboard/constants";
import CalendarIcon from "../../assets/images/icons/calendar.svg";
import { ActionButtonsGroup } from "../buttonsGroup/ActionButtonsGroup";
import {
  selectPeriodRequest,
  setStartDateRequest,
  setEndDateRequest,
} from "../../redux/selectedPeriods/actions";
import endPoints from "../../services/apiEndPoints";
import { getData } from "../../services";

const useStyle = makeStyles((theme) => ({
  timePeriodContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonsGroup: {
    width: "fit-content",
    marginLeft: "auto",
  },
  divider: {
    height: 24,
    color: COLORS.darks[0][25],
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(2),
  },
  groupIcons: {
    width: "fit-content",
    marginLeft: "auto",
    height: "fit-content",
  },
  iconButton: {
    marginLeft: theme.spacing(2),
  },
  datePicker: {
    width: "max-content",
    position: "absolute",
    top: "30px",
    right: 0,
    zIndex: 1,
  },
  timeRangeText: {
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  toggle: {
    width: "fit-content",
    marginRight: "auto",
    marginLeft: theme.spacing(2),
  },
}));

const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const showDates = (period, startDate, endDate) => {
  const date = new Date();
  const { user } = useSelector((state) => state.auth);
  const currentMonth =
    date.toLocaleString("en-us", { month: "short" }) + " " + date.getUTCFullYear();
  const [meterDate, setMeterDate] = useState();
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    getData(endPoints.METER_START_DATE, (data) => {
      const firstDate = data.first_date ? new Date(data.first_date) : null;
      if (firstDate) {
        setMeterDate({
          month: firstDate.toLocaleString("en-us", { month: "short" }),
          year: firstDate.getUTCFullYear(),
        });
      }
    });
  }, [cluster, segments, selectedCountries]);

  switch (period.name) {
    case PERIODS[0].name:
      return meterDate ? `Jan ${date.getUTCFullYear()} - ${currentMonth}` : " ";
    case PERIODS[1].name:
      if (user.is_demo) return `Jan 2022 - ${currentMonth}`;
      return meterDate ? `${meterDate.month} ${meterDate.year} - ${currentMonth}` : " ";
    case PERIODS[2].name:
      if (user.is_demo) return "Jan 2022 - Jan 2042";
      return meterDate
        ? `${meterDate.month} ${meterDate.year} - ${meterDate.month} ${meterDate.year + 20}`
        : " ";
    case PERIODS[3].name:
      if (startDate && endDate) {
        const startDateMonth = startDate.toLocaleString("en-us", {
          month: "short",
        });
        const endDateMonth = endDate.toLocaleString("en-us", {
          month: "short",
        });
        return `${startDateMonth} ${startDate.getUTCFullYear()} - ${endDateMonth} ${endDate.getUTCFullYear()}`;
      }
      return "";
    default:
      return;
  }
};

export const ActionTimeGroup = ({
  titleSize,
  hideShareBtn,
  hideExportBtn,
  hideIntegrateBtn,
  hideTimeRange,
  TimeRangeComponent,
  containerStyle,
  showToggleFilter,
  toggleFilter,
  toggleOptions,
  setToggleFilter,
  buttonsGroupStyle,
}) => {
  const classes = useStyle();
  const ref = useRef();
  const dispatch = useDispatch();
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const { period, startDate, endDate } = useSelector((state) => state.selectedPeriods);
  const [dates, setDates] = useState(startDate && endDate ? [startDate, endDate] : []);

  const handleChange = (dates) => {
    setDates(dates);
    const [start, end] = dates;
    dispatch(setStartDateRequest(new Date(start)));
    if (end) {
      dispatch(setEndDateRequest(new Date(end)));
      ref.current.closeCalendar();
      dispatch(selectPeriodRequest(PERIODS[3]));
    }
  };

  const changePeriod = (p) => {
    dispatch(selectPeriodRequest(p));
    setDates([]);
  };

  const defaultDate = () => {
    const date = new Date();
    const currentMonth = date.toLocaleString("en-us", { month: "short" });
    return `Jan ${date.getUTCFullYear()} - ${currentMonth} ${date.getUTCFullYear()}`;
  };

  return (
    <Grid
      className={classes.timePeriodContainer}
      container
      justifyContent="space-between"
      alignItems="center"
      style={containerStyle}
    >
      {!hideTimeRange && !!TimeRangeComponent ? (
        <TimeRangeComponent value={showDates(period) || defaultDate()} />
      ) : !hideTimeRange ? (
        <Typography className={classes.timeRangeText} variant={titleSize}>
          {showDates(period, startDate, endDate) || defaultDate()}
        </Typography>
      ) : null}

      {showToggleFilter ? (
        <Grid container className={classes.toggle}>
          <Toggle
            value={toggleFilter}
            options={toggleOptions || [currencySymbol, "kWh"]}
            onChanged={setToggleFilter}
          />
        </Grid>
      ) : null}
      <Grid
        className={classes.buttonsGroup}
        container
        style={buttonsGroupStyle}
        alignItems="center"
      >
        {!hideShareBtn || !hideExportBtn || !hideIntegrateBtn ? (
          <>
            <ActionButtonsGroup
              hideShareBtn={hideShareBtn}
              hideExportBtn={hideExportBtn}
              hideIntegrateBtn={hideIntegrateBtn}
            />
            <Divider className={classes.divider} orientation="vertical" />
          </>
        ) : null}
        <Grid className={classes.buttonsGroup} container alignItems="center">
          {PERIODS.map((p, index) =>
            p.show ? (
              <SmallRectangleButton
                key={index}
                text={p.name}
                active={p.name === period.name}
                onClick={() => changePeriod(p)}
              />
            ) : null
          )}
          <Grid item>
            <DatePicker
              value={dates}
              className="purple"
              weekDays={weekDays}
              maxDate={new Date()}
              months={months}
              ref={ref}
              range
              render={(value, openCalendar) => {
                return <Icon hoverable src={CalendarIcon} onClick={openCalendar} />;
              }}
              calendarPosition="bottom-right"
              fixMainPosition
              numberOfMonths={2}
              onChange={handleChange}
              weekStartDayIndex={1}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

ActionTimeGroup.defaultProps = {
  titleSize: "h5",
  showToggleFilter: false,
};
